/**
 * EVERY figure the overview returned, as rows.
 *
 * Nothing is hidden by the six-tile strip: a strip of twenty-two tiles is not a
 * strip, so the headline six are tiles and the whole set is here. It iterates
 * `Object.entries(kpis)`, so a figure added to the read model appears without an
 * edit — which is the property the tile list deliberately does not have.
 *
 * THE `Counted` COLUMN IS THE POINT OF THE TABLE. Without it, a window total, a
 * standing shelf figure and a live count sit in one list looking identical, and
 * only one of the three can honestly be captioned "in this period".
 */
import React, { useId, useState } from 'react';
import { AnalyticsOverview } from '../../../../../api/analytics-client';
import { PanelShell, ChartShellState } from '../charts/chart-shell';
import { DataTable, PanelToggle, TableScroller, Td, Th } from '../charts/chart.styled';
import { format } from '../charts/chart-theme';
import { COUNTED_AS } from '../data/panel-plan';
import { kpiLabel } from '../chrome/kpi-strip';
import { Collapse } from '../../../../../motion';

export interface AllFiguresPanelProps {
  state: ChartShellState;
  overview?: AnalyticsOverview;
  onExportCsv?: () => void;
}

export const AllFiguresPanel: React.FC<AllFiguresPanelProps> = ({
  state,
  overview,
  onExportCsv,
}) => {
  // Collapsed by default: it is the reference, not the headline.
  const [open, setOpen] = useState(false);
  // Unique per instance: Overview and Trends both mount this panel and stay
  // mounted, so a fixed id would be two ids in one document.
  const regionId = useId();
  const rows = overview ? Object.entries(overview.kpis) : [];

  return (
    <PanelShell
      id="all-figures"
      title="Every figure in this period"
      wide
      state={state}
      onExportCsv={onExportCsv}
      caveat="The tiles above are the headline; this is the whole set the server returned, including the ones counted as of now rather than over the period."
      emptyReason={
        overview && !rows.length
          ? {
              title: 'No figures were returned',
              text: 'The server answered without any figures, so there is nothing to show and nothing to infer.',
            }
          : null
      }
      controls={
        <PanelToggle
          type="button"
          active={open}
          aria-expanded={open}
          aria-controls={regionId}
          data-testid="all-figures-toggle"
          onClick={() => setOpen(value => !value)}
        >
          {open ? 'Hide' : `Show all ${rows.length}`}
        </PanelToggle>
      }
    >
      {/* The table unfolds rather than appearing: the height animates open and
          closed, and the rows below the panel slide instead of jumping. */}
      <Collapse open={Boolean(open && overview)} id={regionId}>
        <TableScroller>
          <DataTable data-testid="all-figures-table">
            <thead>
              <tr>
                <Th scope="col">Figure</Th>
                <Th scope="col" numeric>
                  Value
                </Th>
                <Th scope="col" numeric>
                  Previous
                </Th>
                <Th scope="col" numeric>
                  Change
                </Th>
                <Th scope="col">Counted</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([key, kpi]) => (
                <tr key={key}>
                  <Td>{kpiLabel(key)}</Td>
                  <Td numeric>{kpi.value.toLocaleString()}</Td>
                  <Td numeric>
                    {kpi.compareValue == null ? '—' : kpi.compareValue.toLocaleString()}
                  </Td>
                  <Td numeric>{format.delta(kpi.delta)}</Td>
                  <Td>{COUNTED_AS[kpi.kind]}</Td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </TableScroller>
      </Collapse>
    </PanelShell>
  );
};
